import { yupResolver } from '@hookform/resolvers/yup';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog.tsx';
import { FormActions } from '@openx/components-core/lib/FormActions/FormActions.tsx';
import { FormContainer } from '@openx/components-core/lib/FormContainer/FormContainer.tsx';
import { scrollToError } from '@openx/components-core/utils/scrollToError.ts';
import { difference } from '@openx/components-form/difference.ts';
import { PageHeader } from '@openx/components-header/PageHeader.tsx';
import type { OrganizationData } from '@openx/types/organization.ts';
import isEmpty from 'lodash/isEmpty';
import { useMemo, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import type { OrganizationFormData } from '../../types.ts';
import { useOrganizationContext } from '../../utils/context/OrganizationContext.tsx';
import { OpenAudienceAccessSection } from './sections/OpenAudienceAccessSection.tsx';
import { OrganizationDetailsSections } from './sections/OrganizationDetailsSection.tsx';
import { PixelSection } from './sections/PixelSection.tsx';
import { transformDataToMatchOrganizationForm, transformEditFormDataToApiPayload } from './utils.ts';
import { getValidationSchema } from './validationSchema/validationSchema.ts';

type OrganizationEditFormProps = {
  currentOrganization: OrganizationData;
};

export const OrganizationEditForm = ({ currentOrganization }: OrganizationEditFormProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const initialValues = transformDataToMatchOrganizationForm(currentOrganization);
  const { useEditOrganization } = useOrganizationContext();
  const { editOrganization, loading } = useEditOrganization();

  const formMethods = useForm<OrganizationFormData>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: yupResolver(getValidationSchema(t)),
  });

  const initialFormValues = useRef<OrganizationFormData>(initialValues);

  const { handleSubmit, watch } = formMethods;
  const { open: openConfirmationDialog, close: closeConfirmationDialog } = useConfirmationDialog();

  const goBackPath = location.state?.settingsGoBackPath || '/organizations';
  const isFormUnchanged = useMemo(() => isEmpty(difference(initialValues ?? {}, watch())), [initialValues, watch]);

  const submitOrganizationEdit = async (formValues: OrganizationFormData) => {
    const currentFeatureFlags = currentOrganization.feature_flags.map(({ value }) => value);
    const payload = transformEditFormDataToApiPayload(formValues, currentFeatureFlags);

    await editOrganization({
      data: payload,
      id: currentOrganization.id,
    });
  };

  const onSubmit = async (formValues: OrganizationFormData) => {
    const isRetargetingBeingDisabled = !!currentOrganization.retargeting_enabled && !formValues.retargeting_enabled;

    if (!isRetargetingBeingDisabled) {
      await submitOrganizationEdit(formValues);
      return;
    }

    openConfirmationDialog({
      confirmationButtonColor: 'error',
      confirmationLabel: t('Disable'),
      content: (
        <DialogContent dividers>
          <Typography>
            {t(
              'All segments must be deleted to disable this feature. Disabling Retargeting will remove these features.'
            )}
          </Typography>
        </DialogContent>
      ),
      customContent: true,
      onCancel: closeConfirmationDialog,
      onConfirmed: async () => {
        closeConfirmationDialog();
        await submitOrganizationEdit(formValues);
      },
      title: t('Disable Retargeting'),
    });
  };

  return (
    <PageHeader title={t('Edit: {organization}', { organization: currentOrganization.name })} titlePrefix="OpenXSelect">
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit, scrollToError)}>
          <FormContainer fullPage>
            <OrganizationDetailsSections isEdit={true} initialFormValues={initialFormValues.current} />

            <OpenAudienceAccessSection isEdit={true} />

            <Divider />

            <PixelSection />

            <FormActions
              submitButtonLabel={t('Save')}
              cancelButtonLabel={t('Cancel')}
              onCancel={() => navigate(goBackPath)}
              disabled={isFormUnchanged || loading}
              isSubmitting={loading}
            />
          </FormContainer>
        </form>
      </FormProvider>
    </PageHeader>
  );
};
